import { useState, useEffect } from 'react';
import { Upload, CheckCircle, AlertCircle, X, Star, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUpload } from './UploadContext';

const CustomAlert = ({ title, description }) => (
  <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-4 rounded">
    <p className="font-bold">{title}</p>
    <p>{description}</p>
  </div>
);

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit(rating, comment);
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Questions Generated Successfully!
        </h2>

        <div className="mb-6">
          <p className="text-gray-600 mb-3 text-center font-medium">Rate your experience:</p>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= (hoveredRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2">
            Comments (Optional):
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your feedback..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            rows="4"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0}
            className={`w-full px-6 py-3 rounded-lg transition-colors duration-300 font-medium ${
              rating === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              </div>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const CandidateLandingPage = () => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(90);
  const [isResumeUploaded, setIsResumeUploaded] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState(null);
  const [isQuestionsGenerated, setIsQuestionsGenerated] = useState(false);
  const { uploadId, setUploadId } = useUpload();

  useEffect(() => {
    let timer;
    if (isUploading && timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0) {
      setIsUploading(false);
      setUploadStatus('error');
    }
    return () => clearTimeout(timer);
  }, [isUploading, timeLeft]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setUploadStatus(null);
    setIsResumeUploaded(false);
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadStatus('error');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/parse-daf/api/v1/upload`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setUploadStatus('success');
        setIsResumeUploaded(true);
        setUploadId(data['user_id']);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateDafQuestions = async () => {
    setIsGeneratingQuestions(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/generate-question/api/v1/user/${uploadId}/question`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        setGeneratedPdfBlob(blob);
        setIsQuestionsGenerated(true);
      } else {
        throw new Error('Failed to generate questions');
      }
    } catch (error) {
      alert('Failed to generate DAF questions. Please try again.');
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const handleDownloadAndShowFeedback = () => {
    if (generatedPdfBlob) {
      const url = window.URL.createObjectURL(generatedPdfBlob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'generated_questions.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setShowFeedbackModal(true);
    }
  };

  const handleFeedbackSubmit = async (rating, comment) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/user/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: uploadId,
            rating: String(rating),
            comment: comment
          })
        }
      );

      if (response.ok) {
        setShowFeedbackModal(false);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      alert('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center items-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full"
      >
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Welcome, Candidate!</h1>
        <p className="text-gray-600 mb-8 text-center">
          We're excited to learn more about you. Please upload your detailed application form to get started with the interview process.
        </p>

        <div className="mb-8">
          <label
            htmlFor="resume-upload"
            className="flex flex-col items-center px-4 py-6 bg-white text-blue rounded-lg shadow-lg tracking-wide uppercase border border-blue cursor-pointer hover:bg-blue-50 transition duration-300 ease-in-out"
          >
            <Upload className="w-8 h-8 text-indigo-600" />
            <span className="mt-2 text-base leading-normal text-gray-600">Select your DAF</span>
            <input
              id="resume-upload"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx"
            />
          </label>
          {file && (
            <p className="mt-2 text-sm text-gray-600 text-center">
              Selected file: {file.name}
            </p>
          )}
        </div>

        <motion.button
          initial={{ opacity: 1 }}
          whileHover={{ scale: isResumeUploaded ? 1.0 : 1.05 }}
          whileTap={{ scale: isResumeUploaded ? 1.0 : 0.95 }}
          className={`w-full px-6 py-3 rounded-lg transition-colors duration-300 flex items-center justify-center ${
            isResumeUploaded
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
          onClick={handleUpload}
          disabled={isUploading || isResumeUploaded}
        >
          {isUploading ? (
            <>
              <span className="mr-2">Uploading...</span>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
            </>
          ) : isResumeUploaded ? (
            <>
              <CheckCircle className="mr-2" />
              Resume Uploaded
            </>
          ) : (
            <>
              <Upload className="mr-2" />
              Upload Resume
            </>
          )}
        </motion.button>

        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4"
            >
              <CustomAlert
                title="Uploading in progress"
                description={`Please wait while we process your file. Time remaining: ${timeLeft} seconds`}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {uploadStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-4 p-2 bg-red-100 text-red-700 rounded-lg flex items-center justify-center"
            >
              <AlertCircle className="mr-2" />
              {file ? 'Upload failed. Please try again.' : 'Please select a file before uploading.'}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          whileHover={{ scale: isResumeUploaded && !isGeneratingQuestions ? 1.05 : 1.0 }}
          whileTap={{ scale: isResumeUploaded && !isGeneratingQuestions ? 0.95 : 1.0 }}
          className={`w-full px-6 py-3 rounded-lg transition-colors duration-300 flex items-center justify-center mt-4 font-medium ${
            isResumeUploaded
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={isQuestionsGenerated ? handleDownloadAndShowFeedback : handleGenerateDafQuestions}
          disabled={!isResumeUploaded || isGeneratingQuestions}
        >
          {isGeneratingQuestions ? (
            <>
              <span className="mr-2">Generating, Please Wait!</span>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
            </>
          ) : isQuestionsGenerated ? (
            <>
              <Download className="mr-2" />
              Download Questions
            </>
          ) : (
            'Generate DAF Questions'
          )}
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
            onSubmit={handleFeedbackSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CandidateLandingPage;
